import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ComingSoon } from '../components/ComingSoon';
import { MarkPaidDialog } from '../components/MarkPaidDialog';
import { PaidIcon, PrintIcon, VoidIcon, MailIcon, DownloadIcon } from '../components/icons';
import { StatementDocument } from '../components/StatementDocument';
import { VoidDialog } from '../components/VoidDialog';
import { describeRecordFailure } from '../errors';
import { recordWritesAvailableInBuild } from '../format';
import { contractorPaymentRecordsGateway as defaultGateway, type ContractorPaymentRecordsGateway } from '../index';
import type { PaymentRecordDetail, PaymentStatement } from '../types';
import { IdentityContext } from '../../identity/state/IdentityContext';
import { requestNotificationsRefresh } from '../../notifications/useStoredNotifications';
import '../contractor-payments.css';

type Load = { kind: 'loading' } | { kind: 'error'; message: string; notFound: boolean } | { kind: 'loaded'; statement: PaymentStatement; record: PaymentRecordDetail };
type Dialog = { kind: 'paid' } | { kind: 'void' } | null;

export const LIST_PATH = '/payroll/contractor-payments';

/**
 * Contractor Payments statement preview (Figma S06-S16 chrome with the statement body). Paid, Approved and Voided differ only by the status pill, the dated
 * note, the Voided banner and the three VOIDED watermarks; every state carries the "not a check" watermark. "Back to payment list" returns to the list with
 * the filters it had. Print is the browser's print of the statement alone (print CSS hides the app chrome). Download PDF and Email are visible but disabled
 * until the server-side PDF and email phase (BACKEND GAP). Mark as Paid and Void are offered only when the server says this caller may.
 */
export function StatementPreviewPage({ gateway = defaultGateway }: { gateway?: ContractorPaymentRecordsGateway }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const printed = useRef(false);
  const listSearch = (location.state as { listSearch?: string } | null)?.listSearch ?? '';
  const writes = recordWritesAvailableInBuild(import.meta.env);

  const me = useContext(IdentityContext)?.me ?? null;
  const companyName = me?.memberships.find((m) => m.companyId === me.activeCompanyId)?.companyName ?? null;

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    Promise.all([gateway.statement(id, controller.signal), gateway.get(id, controller.signal)])
      .then(([statement, record]) => { if (!controller.signal.aborted) setLoad({ kind: 'loaded', statement, record }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const failure = describeRecordFailure(err, 'The payment statement couldn’t be loaded. Try again.');
        setLoad({ kind: 'error', message: failure.message, notFound: failure.notFound });
      });
    return () => controller.abort();
  }, [gateway, id, nonce]);

  useEffect(() => {
    if (load.kind !== 'loading') headingRef.current?.focus();
  }, [load.kind]);

  // "Print" from the list opens this page with ?print=1: print once, then drop the flag so a reload never prints again.
  useEffect(() => {
    if (load.kind === 'loaded' && params.get('print') === '1' && !printed.current) {
      printed.current = true;
      const next = new URLSearchParams(params);
      next.delete('print');
      setParams(next, { replace: true, state: location.state });
      const t = window.setTimeout(() => window.print(), 150);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [load.kind, params, setParams, location.state]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const record = load.kind === 'loaded' ? load.record : null;
  const canPay = writes && record?.allowedActions.includes('markPaid') === true;
  const canVoid = writes && record?.allowedActions.includes('void') === true;

  async function markPaid(paidOn: string, reference: string | null): Promise<string | null> {
    if (!id) return null;
    try {
      await gateway.markPaid(id, paidOn, reference);
    } catch (err) {
      return describeRecordFailure(err, 'The payment couldn’t be marked as paid. Try again.').message;
    }
    setDialog(null);
    setNotice('Marked as Paid. This is a record only: no money was moved.');
    reload();
    return null;
  }

  async function voidIt(reason: string): Promise<string | null> {
    if (!id) return null;
    try {
      await gateway.voidRecord(id, reason);
    } catch (err) {
      return describeRecordFailure(err, 'The payment couldn’t be voided. Try again.').message;
    }
    setDialog(null);
    setNotice('Payment voided. The record is kept for the audit trail.');
    requestNotificationsRefresh();
    reload();
    return null;
  }

  return (
    <section className="cp-page cp-preview" aria-labelledby="cp-preview-heading" aria-busy={load.kind === 'loading'}>
      <div className="cp-head no-print">
        <h2 id="cp-preview-heading" ref={headingRef} tabIndex={-1} className="cp-title">Payment Preview</h2>
      </div>
      <div className="cp-preview__bar no-print">
        <button type="button" className="cp-btn cp-btn--outline" onClick={() => navigate({ pathname: LIST_PATH, search: listSearch })}>Back to payment list</button>
        <div className="cp-preview__actions">
          {canPay && <button type="button" className="cp-btn cp-btn--outline" onClick={() => setDialog({ kind: 'paid' })}><PaidIcon /> Mark as Paid</button>}
          {canVoid && <button type="button" className="cp-btn cp-btn--outline cp-btn--void" onClick={() => setDialog({ kind: 'void' })}><VoidIcon /> Void</button>}
          <ComingSoon label="Email statement" reason="Email is coming soon" className="cp-btn cp-btn--outline"><MailIcon /> Email</ComingSoon>
          <ComingSoon label="Download PDF" reason="Download PDF is coming soon" className="cp-btn cp-btn--outline"><DownloadIcon /> Download PDF</ComingSoon>
          <button type="button" className="cp-btn cp-btn--primary" disabled={load.kind !== 'loaded'} onClick={() => window.print()}><PrintIcon /> Print</button>
        </div>
      </div>

      {notice && <p className="success cp-notice no-print" role="status" data-testid="preview-notice">{notice}</p>}
      {load.kind === 'loading' && <p className="cp-muted no-print" role="status">Loading payment statement…</p>}
      {load.kind === 'error' && (
        <div className="cp-error no-print" role="alert">
          <p className="error" data-testid="preview-error">{load.notFound ? 'This payment record is not available.' : load.message}</p>
          {load.notFound ? <Link to={{ pathname: LIST_PATH, search: listSearch }}>Back to payment list</Link> : <button type="button" className="cp-btn cp-btn--outline" onClick={reload}>Try again</button>}
        </div>
      )}
      {load.kind === 'loaded' && <StatementDocument statement={load.statement} fallbackCompanyName={companyName} />}

      {dialog?.kind === 'paid' && record && <MarkPaidDialog record={record} onClose={() => setDialog(null)} onConfirm={markPaid} />}
      {dialog?.kind === 'void' && record && <VoidDialog record={record} onClose={() => setDialog(null)} onConfirm={voidIt} />}
    </section>
  );
}
